import {BlockItemDrawProps} from "../FrameBlockItem";
import {BitmapText, IBitmapTextStyle, Matrix, Texture} from "pixi.js";
import {inches, lineBlueHeavy} from "../../../const";
import {ClickableFace, drawFace} from "./ClickableFace";
import {glassSize, liteGlass} from "../../../model/glass/MdGlass";
import {fitGlassLabel, glassLabelText} from "../../../shared-2d/GlassLabel";
import type {ElevationApp} from "../../../ElevationApp";

const TEXT_STYLE: Partial<IBitmapTextStyle> = {
    fontName: "Arial",
    fontSize: 40,
};

// the glass is inset from the cell bounds by 1/2" on every side.
const INSET = 0;

// how far the reference mark sits in from the lite's top-left corner.
const MARK_INSET = 1.5 * inches;

// lazily-built glass gradient texture, shared across every LiteFace. A diagonal
// pale-blue wash with a brighter sheen band reads as glass and sits a touch more
// saturated than the pale `backgroundBlue` so the window stands out against it.
let glassTexture: Texture | null = null;

export function getGlassTexture(): Texture {
    if (glassTexture) return glassTexture;

    const size = 256;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext("2d")!;
    const grad = ctx.createLinearGradient(0, 0, size, size);
    grad.addColorStop(0, "#DEEAF8");
    grad.addColorStop(0.35, "#ECF3FC");
    grad.addColorStop(0.5, "#F6FAFE");
    grad.addColorStop(0.65, "#ECF3FC");
    grad.addColorStop(1, "#E2EDF9");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);

    glassTexture = Texture.from(canvas);
    return glassTexture;
}

export class LiteFace extends ClickableFace {
    // the two labels drawn on the cell: the reference mark ("P1") in the top-left
    // corner and the glass size ("GL w x h") in the middle. Held in a box so the fill
    // closure (built before `this` exists) and destroy() share them. `selected` is
    // the lite whose glass label was last sent to the StatusBar, so destroy() can
    // remove it.
    private labelBox: LabelBox;

    constructor() {
        const labelBox: LabelBox = {label: null, mark: null, selected: null};
        super((props: BlockItemDrawProps) => {
            const g = props.graphics;
            g.clear();

            // normalize so the rect is well-formed regardless of start/end order.
            const left = Math.min(props.startX, props.endX) + INSET;
            const right = Math.max(props.startX, props.endX) - INSET;
            const top = Math.min(props.startY, props.endY) + INSET;
            const bottom = Math.max(props.startY, props.endY) - INSET;

            const w = right - left;
            const h = bottom - top;
            if (w <= 0 || h <= 0) return;

            const texture = getGlassTexture();

            // map the (size x size) gradient onto the rect: scale to the rect's
            // dimensions and translate to its top-left so it doesn't tile.
            const matrix = new Matrix();
            matrix.scale(w / texture.width, h / texture.height);
            matrix.translate(left, top);

            g.lineStyle(2, lineBlueHeavy, 1);
            g.beginTextureFill({texture, matrix});
            drawFace(g, props, left, top, w, h);
            g.endFill();

            // the reference mark, in the top-left corner so it never lands on the
            // centred size label. Only on the exports (see showLiteMarks). A slope
            // on the top or left moves it in by what the slope takes there.
            if(!labelBox.mark) labelBox.mark = newLabel();
            const mark = labelBox.mark;
            if(mark.parent !== g) g.addChild(mark);
            mark.anchor.set(0, 0);
            mark.visible = props.elev.viewSettings.showLiteMarks.value;
            mark.text = props.block.kind === "lite" ? (props.block.mark || "") : "";
            const corner = props.polygon?.[0] ?? {x: left, y: top};
            mark.position.set(corner.x + MARK_INSET, corner.y + MARK_INSET);

            // the cell footprint is the cut-out (its edges pin to the rabbets), so
            // the glass size reads straight off it. Shown while the cell is selected:
            // always in the StatusBar, and on the cell only when it fits there.
            if(!labelBox.label) {
                const label = newLabel();
                label.anchor.set(0.5, 0.5);
                labelBox.label = label;
            }
            const label = labelBox.label;
            // the graphics is cleared each draw but keeps its children; re-parent in
            // case a grid rebuild handed this face a fresh Graphics.
            if(label.parent !== g) g.addChild(label);

            const selectedLabels = props.elev.selectedGlassLabels;
            const id = props.block.id;
            if(!props.elev.viewSettings.isSelected(id)) {
                label.visible = false;
                selectedLabels.clear(id);
                return;
            }

            label.text = glassLabelText(props.elev.draw, glassSize(w, h, liteGlass(props.block)));
            fitGlassLabel(label, {x: left, y: top, width: w, height: h}, props.dimensionLines);
            selectedLabels.set(id, label.text);
            labelBox.selected = {elev: props.elev, id};
        });
        this.labelBox = labelBox;
    }

    destroy() {
        for(const label of [this.labelBox.label, this.labelBox.mark]) {
            if(label && !label.destroyed) label.destroy();
        }
        this.labelBox.label = null;
        this.labelBox.mark = null;
        const selected = this.labelBox.selected;
        selected?.elev.selectedGlassLabels.clear(selected.id);
        this.labelBox.selected = null;
        super.destroy();
    }
}

type LabelBox = {
    label: BitmapText | null;
    mark: BitmapText | null;
    selected: {elev: ElevationApp; id: string} | null;
};

// a cell label: inert, so taps fall through to the face and it still selects.
function newLabel(): BitmapText {
    const label = new BitmapText("", TEXT_STYLE);
    label.tint = lineBlueHeavy;
    label.eventMode = "none";
    return label;
}
